import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Chip,
  Box,
  Typography,
} from "@mui/material";

function getInitials(name) {
  if (!name || typeof name !== "string") {
    return "LL";
  }

  const parts = name
    .trim()
    .split(" ")
    .filter(Boolean)
    .slice(0, 2);

  if (parts.length === 0) {
    return "LL";
  }

  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("");
}

function ViewField({ label, value, fullWidth = false }) {
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <Box
        sx={{
          p: 1.75,
          border: "1px solid var(--cc-card-border)",
          borderRadius: 2.5,
          backgroundColor: "var(--cc-card-bg-soft)",
          height: "100%",
          boxShadow: "0 1px 2px rgba(12, 35, 64, 0.06)",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "var(--cc-subtitle)",
            mb: 0.5,
            fontWeight: 700,
            letterSpacing: 0.2,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "var(--app-text)", fontWeight: 600, wordBreak: "break-word" }}
        >
          {value ?? "-"}
        </Typography>
      </Box>
    </Grid>
  );
}

export default function OtherAccountsViewModal({ show, onHide, accountData }) {
  const initials = getInitials(accountData?.AccountName);

  return (
    <Dialog
      open={show}
      onClose={onHide}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          background: "var(--app-surface)",
          color: "var(--app-text)",
          border: "1px solid var(--dg-border)",
        },
      }}
    >
      <Box
        sx={{
          px: 3.5,
          py: 2.5,
          background: "var(--dg-header-gradient)",
          color: "var(--dg-header-text)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.28)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1rem",
              fontWeight: 800,
              letterSpacing: 0.6,
            }}
          >
            {initials}
          </Box>

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {accountData?.AccountName || "Detajet e Llogarisë"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.35 }}>
              Shikim i të dhënave pa mundësi ndryshimi
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Chip
            label={accountData?.IsActive ? "Aktiv" : "Jo Aktiv"}
            color={accountData?.IsActive ? "success" : "default"}
            variant={accountData?.IsActive ? "filled" : "outlined"}
            sx={{
              fontWeight: 800,
              backgroundColor: accountData?.IsActive
                ? "var(--cc-kpi-positive-bg)"
                : "var(--cc-kpi-negative-bg)",
              color: accountData?.IsActive
                ? "var(--cc-kpi-positive-text)"
                : "var(--cc-kpi-negative-text)",
              border: `1px solid ${
                accountData?.IsActive
                  ? "var(--cc-kpi-positive-ring)"
                  : "var(--cc-kpi-negative-ring)"
              }`,
            }}
          />
        </Box>
      </Box>

      <DialogContent
        dividers
        sx={{
          p: 3.5,
          backgroundColor: "var(--app-surface)",
          color: "var(--app-text)",
          borderColor: "var(--dg-border)",
        }}
      >
        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            border: "1px solid var(--cc-card-border)",
            backgroundColor: "var(--cc-card-bg-soft)",
            mb: 2.5,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: "var(--cc-title)", fontWeight: 800, mb: 1.25, letterSpacing: 0.2 }}
          >
            Informata Kryesore
          </Typography>
          <Grid container spacing={1.5}>
            <ViewField label="Emri i Llogarisë" value={accountData?.AccountName} fullWidth />
            <ViewField label="Komenti" value={accountData?.Comment} fullWidth />
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3.5,
          py: 2.25,
          backgroundColor: "var(--cc-card-bg-soft)",
          borderTop: "1px solid var(--cc-card-border)",
        }}
      >
        <Button
          onClick={onHide}
          variant="contained"
          sx={{
            background: "var(--dg-header-gradient)",
            color: "var(--dg-header-text)",
            px: 3.5,
            fontWeight: 700,
            borderRadius: 2,
            "&:hover": {
              background: "var(--dg-header-gradient)",
              filter: "brightness(1.08)",
            },
          }}
        >
          Mbyll
        </Button>
      </DialogActions>
    </Dialog>
  );
}
